import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../../lib/api';
import { toast } from 'sonner';
import { GraduationCap, Plus, X, BookOpen, AlertTriangle, CalendarClock, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { formatDate } from '../../../lib/format';
import { useAuth } from '../../../contexts/AuthContext';

const KPI = ({ icon: Icon, label, value, tone = 'default', testId }) => {
  const tones = { default: 'bg-white border-slate-200', amber: 'bg-amber-50 border-amber-200', red: 'bg-red-50 border-red-200', green: 'bg-emerald-50 border-emerald-200' };
  return (
    <div className={`rounded-sm border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500"><Icon size={13} /> {label}</div>
      <div className="text-2xl font-display font-extrabold mt-2 text-[#1F7A6E]">{value}</div>
    </div>
  );
};

export default function TrainingDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [plans, setPlans] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ plan_name: '', category: '', description: '', default_expiry_months: '' });

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const canWrite = ['super_admin', 'admin', 'hr', 'md'].includes(role) || dept === 'HR' || dept === 'MD';

  const load = () => {
    apiGet('/hr/training/dashboard').then(setD).catch(() => setD({}));
    apiGet('/hr/training/plans').then((r) => setPlans(r.items || [])).catch(() => {});
    apiGet('/hr/training/expiring', { days: 30 }).then((r) => setExpiring(r.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.plan_name.trim()) { toast.error('Plan name is required'); return; }
    try {
      const p = await apiPost('/hr/training/plans', { ...form, default_expiry_months: form.default_expiry_months ? Number(form.default_expiry_months) : null });
      toast.success('Training plan created'); setShowForm(false); setForm({ plan_name: '', category: '', description: '', default_expiry_months: '' });
      nav(`/app/hr/training/plans/${p.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  if (!d) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="training-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People & HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Training & Certifications</h1>
          <p className="text-sm text-slate-600 mt-1">Plan sessions · Record attendance · Track certification expiry.</p>
        </div>
        {canWrite && (
          <button onClick={() => setShowForm(true)} data-testid="new-plan-btn"
            className="px-4 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2"><Plus size={14} /> New Plan</button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={BookOpen} label="Training Plans" value={d.total_plans || 0} testId="kpi-plans" />
        <KPI icon={Award} label="Certifications" value={d.total_certified || 0} tone="green" testId="kpi-certified" />
        <KPI icon={CalendarClock} label="Expiring (30d)" value={d.expiring_count || 0} tone={d.expiring_count ? 'amber' : 'default'} testId="kpi-expiring" />
        <KPI icon={AlertTriangle} label="Expired" value={d.expired_count || 0} tone={d.expired_count ? 'red' : 'default'} testId="kpi-expired" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Expiring certifications */}
        <Card data-testid="expiring-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2"><AlertTriangle size={15} /> Expiring / Expired Certifications</CardTitle>
          </CardHeader>
          <CardContent>
            {expiring.length === 0 ? <div className="text-xs text-slate-400 py-6 text-center">All certifications valid. ✓</div> : (
              <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {expiring.map((r) => (
                  <div key={r.id} className="py-2 flex items-center justify-between text-xs" data-testid={`expiring-${r.id}`}>
                    <div>
                      <div className="font-display font-bold">{r.employee_name}</div>
                      <div className="text-slate-500">{r.plan_name} · {r.session_title}</div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border ${r.status === 'expired' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{r.status === 'expired' ? 'Expired' : r.expiry_date}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming sessions */}
        <Card data-testid="upcoming-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2"><CalendarClock size={15} /> Upcoming Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            {(d.upcoming_sessions || []).length === 0 ? <div className="text-xs text-slate-400 py-6 text-center">No sessions scheduled.</div> : (
              <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {d.upcoming_sessions.map((s) => (
                  <button key={s.session_id} onClick={() => nav(`/app/hr/training/plans/${s.plan_id}`)} data-testid={`upcoming-${s.session_id}`} className="w-full text-left py-2 hover:bg-slate-50 px-2 -mx-2 rounded-sm">
                    <div className="font-display font-bold text-xs">{s.title}</div>
                    <div className="text-[11px] text-slate-500">{s.plan_name} · {s.scheduled_at ? formatDate(s.scheduled_at) : 'TBD'} · <span className="uppercase">{s.trainer_type}</span></div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Plans list */}
      <Card data-testid="plans-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2"><GraduationCap size={15} /> Training Plans</CardTitle></CardHeader>
        <CardContent>
          {plans.length === 0 ? <div className="text-xs text-slate-400 py-6 text-center">No training plans yet.</div> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {plans.map((p) => (
                <button key={p.id} onClick={() => nav(`/app/hr/training/plans/${p.id}`)} data-testid={`plan-card-${p.id}`} className="text-left border border-slate-200 rounded-sm p-3 hover:shadow-md transition-shadow">
                  <div className="font-display font-bold text-sm">{p.plan_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{p.category || 'Uncategorised'}</div>
                  <div className="flex gap-3 mt-2 text-[11px] text-slate-600">
                    <span>{p.session_count} session(s)</span><span>{p.certified_count} certified</span>
                    {p.upcoming_count ? <span className="text-amber-600">{p.upcoming_count} upcoming</span> : null}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="plan-form-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-white rounded-sm w-full max-w-md p-5 space-y-3">
            <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sm">New Training Plan</h3><button type="button" onClick={() => setShowForm(false)}><X size={18} /></button></div>
            <label className="block"><span className="lbl">Plan Name *</span><input data-testid="plan-name-input" className="inp" value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })} /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="lbl">Category</span>
                <select className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="">Select…</option>{['Safety', 'Technical', 'Compliance', 'Quality', 'Soft Skills', 'Induction', 'Statutory'].map((c) => <option key={c} value={c}>{c}</option>)}
                </select></label>
              <label className="block"><span className="lbl">Default Validity (months)</span><input type="number" data-testid="plan-expiry-input" className="inp" value={form.default_expiry_months} onChange={(e) => setForm({ ...form, default_expiry_months: e.target.value })} /></label>
            </div>
            <label className="block"><span className="lbl">Description</span><textarea rows={3} className="inp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <button type="submit" data-testid="plan-submit-btn" className="w-full py-2.5 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c]">Create Plan</button>
          </form>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#1F7A6E}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
    </div>
  );
}
